import { Disclosure, Transition } from '@headlessui/react';
import { Typography, FormValues, SideDrawer } from '@stern-ui/ui';
import clsx from 'clsx';
import { Field, Form, Formik } from 'formik';
import { ChevronDown, ChevronRight, ChevronLeft, Magnify } from 'mdi-material-ui';
import React, { FC, Key, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { nestedItem, selectorObj } from '../types';
import { FetchSelectorType } from '../Utils';

import { SkeletonLoader } from '@/components/Elements';
import {
	savePageNumber,
	saveSearchStudyValue,
	saveStudyId,
	selectSelectorSearchValue,
} from '@/components/PatientDataTable';
import { SelectorContext } from '@/components/Reporting';
import { trackEvent } from '@/lib/analytics';
import { debounce } from '@/utils';

type SelectorContainerProps = {
	handleClick: () => void;
	type: 'study' | 'Trial Site' | 'Study and survey';
};

export const SelectorContainer = (props: SelectorContainerProps) => {
	const { handleClick, type } = props;
	const { fetch, title, saveId, saveName, selectorId, parentItemClickEnabled } =
		FetchSelectorType(type);
	const dispatch = useDispatch();
	const savedId = useSelector(selectorId);
	const search_value = useSelector(selectSelectorSearchValue);
	const { isExpanded } = useContext(SelectorContext);

	const eventOptions = {
		selectorType: type,
	};

	const handleItemClick = (id: string, name: string, trialSiteStudyId?: string) => {
		dispatch(saveId({ id }));
		if (saveName) {
			dispatch(saveName({ name }));
		}
		trialSiteStudyId && dispatch(saveStudyId({ id: trialSiteStudyId }));
		trackEvent('Selector option selected', eventOptions);
	};
	const { data, isLoading } = fetch({
		search_value,
	});

	const schema = Yup.object().shape({
		searchValue: Yup.string(),
	});
	const initialValues = {
		searchValue: search_value || '',
	};

	const saveSearchDebounced = debounce((s: string) => {
		saveSearch && saveSearch(s);
		trackEvent('Selector search used', eventOptions);
	});

	const saveSearch = (searchValue: string) => {
		dispatch(saveSearchStudyValue({ searchValue: searchValue }));
		dispatch(savePageNumber({ pageNumber: 1 }));
	};

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const searchValue = e.target.value;
		saveSearchDebounced(searchValue);
	};

	const handleSearchSubmit = (values: FormValues) => {
		const searchValue = values.searchValue;
		saveSearch(searchValue as string);
	};

	const ChildListItem: FC<{
		item: nestedItem;
		parentItem?: nestedItem;
	}> = ({ item, parentItem }) => {
		return (
			<button
				type='button'
				className='block w-full text-left'
				onClick={() =>
					handleItemClick(
						item.id,
						item.name,
						parentItemClickEnabled ? '' : parentItem?.id
					)
				}
			>
				<Typography
					variant='body'
					dataTestId={`study-selector-item-${item.name}`}
					className={clsx(
						'w-full p-3 text-body-md text-primaryTextDark cursor-pointer',
						savedId === item.id ? 'bg-primaryBrandBackground font-bold' : 'font-normal'
					)}
				>
					{item.name}
				</Typography>
			</button>
		);
	};

	const ParentItem: FC<{ item: nestedItem; nestedItems: nestedItem[] }> = ({
		item,
		nestedItems,
	}) => {
		return (
			<Disclosure defaultOpen={nestedItems.filter((elm) => elm.id === savedId).length > 0}>
				{({ open }) => (
					<>
						<Disclosure.Button
							className={clsx(
								'flex w-full justify-between text-left font-medium items-center',
								savedId === item.id ? '' : 'font-normal'
							)}
							data-test-id={`${item.name}`}
							onClick={() =>
								parentItemClickEnabled && handleItemClick(item.id, item.name)
							}
						>
							<Typography
								variant='body'
								className={clsx(
									'flex justify-between w-full p-3 text-body-md text-primaryTextDark cursor-pointer ',
									savedId === item.id
										? 'bg-primaryBrandBackground font-bold'
										: 'font-normal'
								)}
							>
								{item.name}
								<ChevronDown
									className={`${
										open ? 'rotate-180 transform' : ''
									} h-5 w-5 text-tertiaryTextDark`}
								/>
							</Typography>
						</Disclosure.Button>
						<Transition
							enter='transition duration-100 ease-out'
							enterFrom='transform scale-95 opacity-0'
							enterTo='transform scale-100 opacity-100'
							leave='transition duration-75 ease-out'
							leaveFrom='transform scale-100 opacity-100'
							leaveTo='transform scale-95 opacity-0'
						>
							<Disclosure.Panel className='mx-4'>
								{nestedItems.map((elm: nestedItem, i: Key) => (
									<ChildListItem
										item={elm}
										parentItem={item}
										key={i}
									></ChildListItem>
								))}
							</Disclosure.Panel>
						</Transition>
					</>
				)}
			</Disclosure>
		);
	};

	const renderList = () => {
		return data?.results.map((elm: selectorObj, i: Key) => {
			return elm.nestedItems.length > 0 ? (
				<ParentItem
					item={elm.parentItem}
					nestedItems={elm.nestedItems}
					key={i}
				></ParentItem>
			) : (
				<ChildListItem item={elm.parentItem} key={i}></ChildListItem>
			);
		});
	};
	return (
		<SideDrawer
			isExpanded={isExpanded}
			containerClass={`flex flex-col fixed h-[calc(100vh-80px)] px-3 pt-6 mr-[-2px] ${isExpanded ? `min-w-[240px] w-60` : 'min-w-0 w-0'}`}
			buttonClass={`w-6 h-6 text-primaryTextLight rounded-full top-[1.5rem] -right-[0.8rem] z-10 ${isExpanded ? 'bg-secondaryTextDark' : 'bg-primaryBrand'}`}
			handleClick={handleClick}
			name={`${title} selector`}
			openIcon={<ChevronRight className={'w-4 h-4'} />}
			closeIcon={<ChevronLeft className={'w-4 h-4'} />}
			dataTestId='selectorContainer'
		>
			<div
				className={clsx(
					'transition-visibility ease-linear delay-300 duration-300 overflow-x-hidden',
					isExpanded ? 'w-auto opacity-100 visible ' : 'w-0 opacity-0 invisible'
				)}
			>
				<Typography
					variant='heading_5_medium'
					className='font-semibold text-primaryTextDark'
					dataTestId='studySelectorTitle'
				>
					{title} name
				</Typography>
				<div className='flex mt-8 mb-6'>
					<label htmlFor='search-study' className='sr-only'>
						{title} Search Value
					</label>
					<Formik
						initialValues={initialValues}
						schema={schema}
						onSubmit={handleSearchSubmit}
					>
						{({ handleChange }) => (
							<Form>
								<Field
									id='search-study'
									type='text'
									name='searchValue'
									placeholder='Search'
									data-test-id='StudySearchInput'
									className='h-10 mr-[-4px] rounded-sm border-2 border-secondaryBorder w-[calc(100%+16px)]'
									onChange={(ev: React.ChangeEvent<HTMLInputElement>): void => {
										handleChange(ev);
										handleSearchChange(ev);
									}}
								/>
							</Form>
						)}
					</Formik>
					<div className='flex flex-row justify-center w-12 h-10 items-center text-primaryTextLightwhite bg-primaryBrand rounded-sm'>
						<Magnify className='w-4 h-4 text-white' />
					</div>
				</div>
				{isLoading ? (
					<>
						<SkeletonLoader className='h-6 w-full rounded-sm mb-4' />
						<SkeletonLoader className='h-6 w-full max-w-[170px] rounded-sm mb-4' />
						<SkeletonLoader className='h-6 w-full max-w-[170px] rounded-sm mb-4' />
						<SkeletonLoader className='h-6 w-full max-w-[170px] rounded-sm mb-4' />
						<SkeletonLoader className='h-6 w-full rounded-sm mb-4' />
						<SkeletonLoader className='h-6 w-full max-w-[170px] rounded-sm mb-4' />
						<SkeletonLoader className='h-6 w-full max-w-[170px] rounded-sm mb-4' />
						<SkeletonLoader className='h-6 w-full max-w-[170px] rounded-sm mb-4' />
						<SkeletonLoader className='h-6 w-full rounded-sm mb-4' />
						<SkeletonLoader className='h-6 w-full max-w-[170px] rounded-sm mb-4' />
						<SkeletonLoader className='h-6 w-full max-w-[170px] rounded-sm mb-4' />
						<SkeletonLoader className='h-6 w-full max-w-[170px] rounded-sm mb-4' />
					</>
				) : null}
				{data?.results ? renderList() : null}
			</div>
		</SideDrawer>
	);
};
